import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { AdhocClient } from '../../models/adhoc-service.model';
import { ClientService } from '../../services/client.service';

export interface ClientSelectDialogData {
  clients: AdhocClient[];
}

@Component({
  selector: 'app-client-select-dialog',
  templateUrl: './client-select-dialog.component.html',
  styleUrls: ['./client-select-dialog.component.scss']
})
export class ClientSelectDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  searchControl = new FormControl<string>('');
  allClients: AdhocClient[] = [];
  filteredClients: AdhocClient[] = [];
  selectedClient: AdhocClient | null = null;
  clientPhotos: Map<number, string> = new Map();
  fundingFilter: 'hcp' | 'hpac' | null = null;
  showInactive = false;

  private lastClickTime = 0;
  private lastClickedId: number | null = null;
  private readonly DOUBLE_CLICK_DELAY = 400;

  constructor(
    public dialogRef: MatDialogRef<ClientSelectDialogComponent>,
    private clientService: ClientService,
    @Inject(MAT_DIALOG_DATA) public data: ClientSelectDialogData
  ) {}

  ngOnInit(): void {
    this.allClients = [...this.data.clients].sort((a, b) => {
      const aCount = a.adhoc_count || 0;
      const bCount = b.adhoc_count || 0;
      if (aCount !== bCount) return bCount - aCount;
      return `${a.FirstName} ${a.LastName}`.localeCompare(`${b.FirstName} ${b.LastName}`, undefined, { sensitivity: 'base' });
    });
    this.applyFilters();

    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.filterClients(value || '');
    });

    this.loadAllPhotos();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clientPhotos.forEach(url => URL.revokeObjectURL(url));
  }

  toggleShowInactive(): void {
    this.showInactive = !this.showInactive;
    this.applyFilters();
  }

  toggleFundingFilter(type: 'hcp' | 'hpac'): void {
    this.fundingFilter = this.fundingFilter === type ? null : type;
    this.applyFilters();
  }

  private applyFilters(): void {
    const term = (this.searchControl.value || '').toLowerCase().trim();
    this.filteredClients = this.allClients.filter(c => {
      if (term && !c.FirstName.toLowerCase().includes(term) && !c.LastName.toLowerCase().includes(term)) {
        return false;
      }
      if (this.fundingFilter === 'hcp' && !c.has_hcp) return false;
      if (this.fundingFilter === 'hpac' && !c.is_hpac) return false;
      if (!this.showInactive && c.Status && c.Status.toLowerCase() !== 'current') return false;
      return true;
    });
  }

  private filterClients(term: string): void {
    this.applyFilters();
  }

  private loadAllPhotos(): void {
    for (const client of this.allClients) {
      this.clientService.getClientPhoto(String(client.ResidentID)).subscribe({
        next: (blob) => {
          this.clientPhotos.set(client.ResidentID, URL.createObjectURL(blob));
        },
        error: () => {}
      });
    }
  }

  getPhoto(residentId: number): string | null {
    return this.clientPhotos.get(residentId) || null;
  }

  onClientClick(client: AdhocClient): void {
    const now = Date.now();
    const isDoubleClick = (now - this.lastClickTime) < this.DOUBLE_CLICK_DELAY
      && this.lastClickedId === client.ResidentID;
    this.lastClickTime = now;
    this.lastClickedId = client.ResidentID;

    if (isDoubleClick) {
      this.dialogRef.close(client);
      return;
    }

    this.selectedClient = client;
  }

  onSelect(): void {
    if (this.selectedClient) {
      this.dialogRef.close(this.selectedClient);
    }
  }

  isSelected(client: AdhocClient): boolean {
    return this.selectedClient?.ResidentID === client.ResidentID;
  }
}
